import { useState, useEffect, useRef } from 'react'
import Cookies from 'universal-cookie'
import {JSONFetcher, DISHtml, LogoCompact} from 'shared'
// Use correct import from shared once this bug is fixed:
// https://github.com/react-bootstrap/react-bootstrap/issues/3645
import {Modal} from 'shared/components/modal'
import classnames from 'classnames'
import { useWindowScroll, useClickAway } from 'react-use';

import {GoogleAnalytics} from './trackers/google_analytics'
import {GoogleTagManager} from './trackers/google_tag_manager'
import {Hotjar} from './trackers/hotjar'

const cookies = new Cookies()

const trackingCookieName = 'betterplace-tracking-accepted'

const sendBrowserEvent = (event_name) => {
  JSONFetcher.post({
    url: '/browser_events',
    jsonObject: {event_name: `${event_name}-v3`},
    success: () => {}
  })
}

export const CookieBanner = (props) => {
  const [trackingAccepted, setTrackingAccepted] = useState(cookies.get(trackingCookieName))
  const [showModal, setShowModal] = useState(false)
  const refCookieBanner = useRef(null)

  const handleCookieChange = (cookie) => {
    if (cookie.name === trackingCookieName) setTrackingAccepted(cookie.value)
  }

  const acceptTracking = () => {
    cookies.set(trackingCookieName, 'yes', {path: '/', expires: (new Date(2099, 1, 1))})
    sendBrowserEvent('tracking-accepted')
  }

  const rejectTracking = () => {
    cookies.set(trackingCookieName, 'no', {path: '/', expires: (new Date(2099, 1, 1))})
    sendBrowserEvent('tracking-rejected')
  }

  const openModal = () => {
    setShowModal(true)
    sendBrowserEvent('tracking-more-information')
  }

  useEffect(() => {
    sendBrowserEvent('tracking-banner-show')
    cookies.addChangeListener(handleCookieChange)
    return () => cookies.removeChangeListener(handleCookieChange)
  }, [])

  if (trackingAccepted === 'yes') return <>
    <GoogleAnalytics {...props} />
    <GoogleTagManager {...props} />
    <Hotjar {...props} />
  </>

  if (trackingAccepted === 'no') return null

  return <div className='generic-cookie-banner bg-turquoise-600' ref={refCookieBanner}>
    <Autorejecter refCookieBanner={refCookieBanner} rejectTracking={rejectTracking}/>
    <div className='cookie-content-wrapper'>
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        content={<DISHtml value={I18n.t('layouts.cookie_banner.information_details_html')} />}
        header={I18n.t('layouts.cookie_banner.information_headline')}
      />

      <div className='row'>
        <div className='col-md-4 d-none d-md-block'>
          <div className='d-flex justify-center' style={{ height: '100%' }}>
            <LogoCompact color='#60B2A4' style={{width: 135}} />
          </div>
        </div>
        <div className='col-md-18 p-x'>
          <h1 className='font-desktop-xl font-phone-sm'>{I18n.t('layouts.cookie_banner.headline')}</h1>
          <p>{I18n.t('layouts.cookie_banner.introduction_text')}</p>
          <p>{I18n.t('layouts.cookie_banner.call_for_action')}</p>
          <div className='d-flex m-y-sm justify-between phone-flex-direction-column'>
            <div className='d-flex m-b-sm'>
              <InfoButton className='flex-grow-1' onClick={openModal} />
            </div>
            <div className='d-flex phone-flex-column-reverse button-wrapper'>
              <RejectButton className='flex-grow-1 m-b-sm' onClick={rejectTracking} />
              <AcceptButton className='flex-grow-1 m-b-sm' onClick={acceptTracking} />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
}

// component to auto-reject cookies some time after page interaction.
// must be unmounted to cancel rejection.
const Autorejecter = ({refCookieBanner, rejectTracking}) => {
  const [pageUsed, setPageUsed] = useState(false)
  const { y } = useWindowScroll()
  const refFirstScroll = useRef(true)

  // track scrolling on page
  useEffect(() => {
    if (refFirstScroll.current) {
      refFirstScroll.current = false
      return
    }
    pageUsed || setPageUsed(true)
  }, [y])

  // track clicks outside cookie banner
  useClickAway(refCookieBanner, () => pageUsed || setPageUsed(true))

  // if either happened, start timeout to reject tracking
  useEffect(() => {
    if (!pageUsed) return
    const timer = setTimeout(() => rejectTracking(), 10000)
    return () => clearTimeout(timer)
  }, [pageUsed])

  return null
}

export const CookieBannerLink = (props) => {
  const handleClick = (event) => {
    event.preventDefault()
    cookies.set(trackingCookieName, 'edit', { path: '/', expires: (new Date(2099, 1, 1)) })
  }

  return <a href='#'
    className={props.className ? props.className : ''}
    onClick={handleClick}>{props.content}</a>
}

const AcceptButton = ({className, ...props}) =>
  <button className={classnames('btn btn-primary btn-large', className)} {...props}>
    <i className='fas fa-check' /> {I18n.t('layouts.cookie_banner.accept_button')}
  </button>

const RejectButton = ({className, ...props}) =>
  <button className={classnames('btn btn-outline btn-large', className)} {...props}>
    {I18n.t('layouts.cookie_banner.reject_button')}
  </button>

const InfoButton = ({className, ...props}) =>
  <button className={classnames('simulated-link', className)} {...props}>
    {I18n.t('layouts.cookie_banner.information_button')}
  </button>
